import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
// import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { 
    PhoneIncoming,
    PhoneOutgoing,
    AlertCircle,
    XCircle,
    PhoneOff,
    BanIcon,
    CheckCircle,
    AlertTriangle,
    PhoneMissedIcon,
    Copy,
    Check,
    ExternalLink,
    RefreshCw,
    Search,
    Globe
} from "lucide-react";
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import type { Call } from '@/api/calls/calls.types';
import type { Assistant } from '@/api/assistants/assistants.types';
import type { PhoneNumberDetails } from '@/api/phone-numbers/phone_numbers.types';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { getAssistantById } from '@/api/assistants/assistants.api';
import { getPhoneNumberById } from '@/api/phone-numbers/phone_numbers.api';
import { CallsTableSkeleton } from "./calls-table-skeleton";
import { FadeId } from "@/components/ui/fade-id";

interface CallsTableProps {
    calls: Call[];
    onRefresh: () => Promise<void>;
    isLoading: boolean;
    currentPage?: number;
    pageSize?: number;
}

export const CallsTable = ({ 
    calls, 
    onRefresh, 
    isLoading,
    currentPage = 1,
    pageSize = 10
}: CallsTableProps) => {
    const navigate = useNavigate();
    const [assistants, setAssistants] = useState<Record<string, Assistant>>({});
    const [phoneNumbers, setPhoneNumbers] = useState<Record<string, PhoneNumberDetails>>({});
    const [copiedCustomerNumber, setCopiedCustomerNumber] = useState<string | null>(null);
    const [loadingAssistants, setLoadingAssistants] = useState<Record<string, boolean>>({});
    const [loadingPhoneNumbers, setLoadingPhoneNumbers] = useState<Record<string, boolean>>({});

    const fetchData = async () => {
        // Get unique IDs, filtering out null values
        const uniqueAssistantIds = [...new Set(calls.map(call => call.assistantId).filter(Boolean))];
        const uniquePhoneNumberIds = [...new Set(calls.map(call => call.phoneNumberId).filter(Boolean))];

        // Mark assistants as loading
        const newLoadingAssistants = { ...loadingAssistants };
        uniqueAssistantIds.forEach(id => {
            if (id && !assistants[id] && !loadingAssistants[id]) {
                newLoadingAssistants[id] = true;
            }
        });
        setLoadingAssistants(newLoadingAssistants);

        // Mark phone numbers as loading
        const newLoadingPhoneNumbers = { ...loadingPhoneNumbers };
        uniquePhoneNumberIds.forEach(id => {
            if (id && !phoneNumbers[id] && !loadingPhoneNumbers[id]) {
                newLoadingPhoneNumbers[id] = true;
            }
        });
        setLoadingPhoneNumbers(newLoadingPhoneNumbers);

        // Fetch assistant details
        for (const id of uniqueAssistantIds) {
            if (id && !assistants[id] && !loadingAssistants[id]) {
                try {
                    const response = await getAssistantById(id);
                    if (response.data && !('error' in response)) {
                        setAssistants(prev => ({
                            ...prev,
                            [id]: response.data as Assistant
                        }));
                    }
                } catch {
                    // Error handling is not needed here
                } finally {
                    setLoadingAssistants(prev => ({
                        ...prev,
                        [id]: false
                    }));
                }
            }
        }

        // Fetch phone number details
        for (const id of uniquePhoneNumberIds) {
            if (id && !phoneNumbers[id] && !loadingPhoneNumbers[id]) {
                try {
                    const response = await getPhoneNumberById(id);
                    if (response.data && !('error' in response)) {
                        setPhoneNumbers(prev => ({
                            ...prev,
                            [id]: response.data as PhoneNumberDetails
                        }));
                    }
                } catch {
                    // Error handling is not needed here
                } finally {
                    setLoadingPhoneNumbers(prev => ({
                        ...prev,
                        [id]: false
                    }));
                }
            }
        }
    };

    useEffect(() => {
        fetchData();
    }, [calls]);

    const getEndReasonBadge = (endReason: string | null) => {
        if (!endReason) return null;

        const reason = endReason.toLowerCase();
        let icon = null;
        let colorClasses = "";
        
        if (reason.includes('did-not-answer')) {
            icon = <PhoneMissedIcon className="h-3 w-3" />;
            colorClasses = "bg-red-50 text-red-700 border-red-200/80 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20";
        } else if (reason.includes('customer-ended')) {
            icon = <CheckCircle className="h-3 w-3" />;
            colorClasses = "bg-emerald-50 text-emerald-700 border-emerald-200/80 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20";
        } else if (reason.includes('assistant-ended')) {
            icon = <CheckCircle className="h-3 w-3" />;
            colorClasses = "bg-green-50 text-green-700 border-green-200/80 dark:bg-green-500/10 dark:text-green-400 dark:border-green-500/20";
        } else if (reason.includes('busy')) {
            icon = <PhoneOff className="h-3 w-3" />;
            colorClasses = "bg-orange-50 text-orange-700 border-orange-200/80 dark:bg-orange-500/10 dark:text-orange-400 dark:border-orange-500/20";
        } else if (reason.includes('failed')) {
            icon = <AlertTriangle className="h-3 w-3" />;
            colorClasses = "bg-rose-50 text-rose-700 border-rose-200/80 dark:bg-rose-500/10 dark:text-rose-400 dark:border-rose-500/20";
        } else if (reason.includes('blocked')) {
            icon = <BanIcon className="h-3 w-3" />;
            colorClasses = "bg-red-50 text-red-700 border-red-200/80 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20";
        } else if (reason.includes('error')) {
            icon = <XCircle className="h-3 w-3" />;
            colorClasses = "bg-red-50 text-red-700 border-red-200/80 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20";
        } else {
            icon = <AlertCircle className="h-3 w-3" />;
            colorClasses = "bg-blue-50 text-blue-700 border-blue-200/80 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20";
        }

        return (
            <div 
                className={`
                    inline-flex items-center gap-1
                    text-xs font-medium px-2 py-1
                    rounded-full whitespace-nowrap border
                    max-w-[14rem] truncate
                    ${colorClasses}
                `}
            >
                {icon}
                <span className="truncate">
                    {endReason
                        .toLowerCase()
                        .split('-')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ')}
                </span>
            </div>
        );
    };

    const copyToClipboard = (value: string) => {
        navigator.clipboard.writeText(value);
        setCopiedCustomerNumber(value);
        setTimeout(() => setCopiedCustomerNumber(null), 2000);
    };

    const getTimeZoneString = () => {
        const offset = new Date().getTimezoneOffset();
        const hours = Math.abs(Math.floor(offset / 60));
        const minutes = Math.abs(offset % 60);
        const sign = offset < 0 ? '+' : '-';
        
        return `(GMT${sign}${hours}${minutes ? `:${minutes}` : ''})`;
    };

    const formatDateTime = (dateString: string | null) => {
        if (!dateString) return '-';
        
        try {
            // Parse the ISO date string
            const date = new Date(dateString);
            
            // Check if date is valid
            if (isNaN(date.getTime())) {
                return '-';
            }
            
            // Format in 12-hour time with AM/PM
            return format(date, 'MMM dd, yyyy, h:mm a');
        } catch (error) {
            console.error('Date formatting error:', error);
            return '-';
        }
    };

    return isLoading ? (
        <CallsTableSkeleton />
    ) : (
        <div className="rounded-md border shadow-sm">
            <Table>
                <TableHeader>
                    <TableRow className="bg-muted/50">
                        <TableHead className="w-[100px] whitespace-nowrap">Type</TableHead>
                        <TableHead className="whitespace-nowrap">Call ID</TableHead>
                        <TableHead className="whitespace-nowrap">Assistant</TableHead>
                        <TableHead className="whitespace-nowrap">Phone Number</TableHead>
                        <TableHead className="whitespace-nowrap">Customer</TableHead>
                        <TableHead className="whitespace-nowrap">End Reason</TableHead>
                        <TableHead className="whitespace-nowrap">Created At {getTimeZoneString()}</TableHead>
                        <TableHead className="whitespace-nowrap">Duration</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {calls.length > 0 ? (
                        calls.map((call) => {
                            const duration = call.startedAt && call.endedAt
                                ? new Date(call.endedAt).getTime() - new Date(call.startedAt).getTime()
                                : 0;
                            
                            const durationInMinutes = Math.floor(duration / 1000 / 60);
                            const durationInSeconds = Math.floor((duration / 1000) % 60);
                            const formattedDuration = duration > 0 
                                ? `${durationInMinutes}:${durationInSeconds.toString().padStart(2, '0')}`
                                : '-';

                            return (
                                <TableRow 
                                    key={call.id}
                                    className="group hover:bg-muted/50 cursor-pointer transition-colors"
                                    onClick={(e) => {
                                        // Navigate to call details without query parameters
                                        navigate(`/dashboard/calls/${call.id}`);
                                    }}
                                >
                                    <TableCell>
                                        <TooltipProvider delayDuration={200}>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                                                        <div 
                                        className={`
                                            inline-flex items-center gap-1.5
                                            text-xs font-medium px-2.5 py-1
                                            ${call.type === 'inboundPhoneCall' 
                                                ? 'bg-primary/10 text-primary hover:bg-primary/20' 
                                                : call.type === 'webCall'
                                                ? 'bg-blue-50 text-blue-700 border border-blue-200/80 hover:bg-blue-100 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20 dark:hover:bg-blue-500/20'
                                                : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                                            }
                                            rounded-full whitespace-nowrap
                                            max-w-[200px] truncate
                                            transition-colors
                                        `}
                                    >
                                        {call.type === 'inboundPhoneCall' 
                                            ? <PhoneIncoming className="h-3 w-3" />
                                            : call.type === 'webCall'
                                            ? <Globe className="h-3 w-3" />
                                            : <PhoneOutgoing className="h-3 w-3" />
                                        }
                                        <span className="text-xs">
                                            {call.type === 'inboundPhoneCall' 
                                                ? 'Inbound' 
                                                : call.type === 'webCall'
                                                ? 'Web Call'
                                                : 'Outbound'}
                                        </span>
                                    </div>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <p>
                                                        {call.phoneCallProvider && call.phoneCallTransport 
                                                            ? `${call.phoneCallProvider} via ${call.phoneCallTransport}`
                                                            : call.type === 'webCall' 
                                                            ? 'Web Call via Browser'
                                                            : 'Phone Call'
                                                        }
                                                    </p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </TableCell>
                                    <TableCell>
                                        <div onClick={(e) => e.stopPropagation()}>
                                            <FadeId
                                                id={call.id}
                                                idWidth="180px"
                                                height="h-7"
                                                fontSize="text-xs"
                                                iconSize="sm"
                                                label="Call ID"
                                                tooltipSide="top"
                                            />
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex flex-col gap-1">
                                            {call.assistantId && loadingAssistants[call.assistantId] ? (
                                                <>
                                                    <div className="h-4 w-24 bg-muted animate-pulse rounded"></div>
                                                    <div onClick={(e) => e.stopPropagation()}>
                                                        <FadeId
                                                            id={call.assistantId}
                                                            idWidth="160px"
                                                            height="h-6"
                                                            fontSize="text-[11px]"
                                                            iconSize="sm"
                                                            label="Assistant"
                                                            tooltipSide="top"
                                                            mode="link"
                                                            linkUrl={`/dashboard/assistants/${call.assistantId}`}
                                                        />
                                                    </div>
                                                </>
                                            ) : call.assistantId && assistants[call.assistantId] ? (
                                                <>
                                                    <span className="font-medium text-xs">
                                                        {assistants[call.assistantId].name}
                                                    </span>
                                                    <div onClick={(e) => e.stopPropagation()}>
                                                        <FadeId
                                                            id={call.assistantId}
                                                            idWidth="160px"
                                                            height="h-6"
                                                            fontSize="text-[11px]"
                                                            iconSize="sm"
                                                            label="Assistant"
                                                            tooltipSide="top"
                                                            mode="link"
                                                            linkUrl={`/dashboard/assistants/${call.assistantId}`}
                                                        />
                                                    </div>
                                                </>
                                            ) : call.assistantId ? (
                                                <>
                                                    <span className="text-xs text-destructive font-medium flex items-center gap-1.5">
                                                        <AlertTriangle className="h-3 w-3" />
                                                        Deleted Assistant
                                                    </span>
                                                    <div onClick={(e) => e.stopPropagation()}>
                                                        <FadeId
                                                            id={call.assistantId}
                                                            idWidth="160px"
                                                            height="h-6"
                                                            fontSize="text-[11px]"
                                                            iconSize="sm"
                                                            label="Assistant"
                                                            tooltipSide="top"
                                                            mode="link"
                                                            linkUrl={`/dashboard/assistants/${call.assistantId}`}
                                                        />
                                                    </div>
                                                </>
                                            ) : (
                                                <span className="text-xs text-muted-foreground">No Assistant</span>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex flex-col gap-1">
                                            {call.phoneNumberId && loadingPhoneNumbers[call.phoneNumberId] ? (
                                                <>
                                                    <div className="h-4 w-24 bg-muted animate-pulse rounded"></div>
                                                    <div onClick={(e) => e.stopPropagation()}>
                                                        <FadeId
                                                            id={call.phoneNumberId}
                                                            idWidth="160px"
                                                            height="h-6"
                                                            fontSize="text-[11px]"
                                                            iconSize="sm"
                                                            label="Phone Number"
                                                            tooltipSide="top"
                                                            mode="link"
                                                            linkUrl={`/dashboard/phone-numbers/${call.phoneNumberId}`}
                                                        />
                                                    </div>
                                                </>
                                            ) : call.phoneNumberId && phoneNumbers[call.phoneNumberId] ? (
                                                <>
                                                    <span className="font-medium text-xs">
                                                        {phoneNumbers[call.phoneNumberId].name}
                                                    </span>
                                                    <div onClick={(e) => e.stopPropagation()}>
                                                        <FadeId
                                                            id={call.phoneNumberId}
                                                            idWidth="160px"
                                                            height="h-6"
                                                            fontSize="text-[11px]"
                                                            iconSize="sm"
                                                            label="Phone Number"
                                                            tooltipSide="top"
                                                            mode="link"
                                                            linkUrl={`/dashboard/phone-numbers/${call.phoneNumberId}`}
                                                        />
                                                    </div>
                                                </>
                                            ) : call.phoneNumberId ? (
                                                <>
                                                    <span className="text-xs text-destructive font-medium flex items-center gap-1.5">
                                                        <AlertTriangle className="h-3 w-3" />
                                                        Deleted Phone Number
                                                    </span>
                                                    <div onClick={(e) => e.stopPropagation()}>
                                                        <FadeId
                                                            id={call.phoneNumberId}
                                                            idWidth="160px"
                                                            height="h-6"
                                                            fontSize="text-[11px]"
                                                            iconSize="sm"
                                                            label="Phone Number"
                                                            tooltipSide="top"
                                                            mode="link"
                                                            linkUrl={`/dashboard/phone-numbers/${call.phoneNumberId}`}
                                                        />
                                                    </div>
                                                </>
                                            ) : (
                                                <span className="text-xs text-muted-foreground">Web Call</span>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex flex-col">
                                            {call.customer ? (
                                                <>
                                                    <div className="flex items-center">
                                                        <span className="font-medium text-xs">{call.customer?.number}</span>
                                                        <TooltipProvider delayDuration={200}>
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            copyToClipboard(call.customer?.number!);
                                                                        }}
                                                                        className="inline-flex h-6 w-6 items-center justify-center rounded-md hover:bg-muted ml-1"
                                                                    >
                                                                        {copiedCustomerNumber === call.customer?.number ? (
                                                                            <Check className="h-3 w-3 text-success" />
                                                                        ) : (
                                                                            <Copy className="h-3 w-3 text-muted-foreground" />
                                                                        )}
                                                                    </button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    {copiedCustomerNumber === call.customer?.number ? 'Copied!' : 'Copy Number'}
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        </TooltipProvider>
                                                    </div>
                                                    {call.customer.extension && (
                                                        <span className="text-xs text-muted-foreground">
                                                            Ext: {call.customer.extension}
                                                        </span>
                                                    )}
                                                </>
                                            ) : (
                                                <span className="text-xs text-muted-foreground">Web Call</span>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        {call.endedReason ? 
                                            getEndReasonBadge(call.endedReason)
                                            : '-'}
                                    </TableCell>
                                    <TableCell>
                                        {call.createdAt ? (
                                            <span className="text-sm whitespace-nowrap">
                                                {formatDateTime(call.createdAt)}
                                            </span>
                                        ) : '-'}
                                    </TableCell>
                                    <TableCell>
                                        <span className="font-medium text-sm">
                                            {formattedDuration}
                                        </span>
                                    </TableCell>
                                </TableRow>
                            );
                        })
                    ) : (
                        <TableRow>
                            <TableCell colSpan={9} className="h-[300px] text-center">
                                <div className="flex flex-col items-center justify-center gap-2">
                                    <Search className="h-12 w-12 text-muted-foreground/50" />
                                    <h3 className="font-semibold text-lg">No results found</h3>
                                    <p className="text-muted-foreground text-sm">
                                        No calls match your current filters. Try adjusting your search criteria.
                                    </p>
                                </div>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </div>
    );
};
